import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import ZendeskIconHideWrapper from "@stadium/services/zendesk/icon-hide-wrapper";
import Button from "@stadium/shared/src/components/shared/button";
import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import HeaderMobileMenuActions from "@components/swagmagic/header-mobile-menu-actions";
import BrandSlider from "@components/swagmagic/header-mobile-nav-menu/brand-slider";
import SlidePanel from "@components/swagmagic/header-mobile-nav-menu/slide-panel";
import { CategoryConfig, HeaderCta, NavCategory } from "@components/swagmagic/header/type";

import { isActiveNavLink } from "@helpers/swagmagic/header";

import { OTHER_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface MenuProps {
  ctaButtonData?: HeaderCta;
  profileText?: string;
  validHeaderNav?: CategoryConfig[];
  overlayRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLDivElement>;
  handleClose: () => void;
}

export default function HeaderMobileNavMenu({
  ctaButtonData,
  profileText,
  validHeaderNav,
  overlayRef,
  menuRef,
  handleClose,
}: Readonly<MenuProps>) {
  const [activeCategory, setActiveCategory] = useState<CategoryConfig | null>(null);
  const [activeSubCategory, setActiveSubCategory] = useState<NavCategory | null>(null);
  const [isBrandListOpen, setIsBrandListOpen] = useState(false);
  const routePathname = usePathname();
  const pathname = routePathname || "";

  useEffect(() => {
    // Handle Escape key to close menu
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const handleOverlayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only close if clicking the overlay itself, not the menu
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleBrandListClose = () => setIsBrandListOpen(false);

  const renderCategoryButton = (category: CategoryConfig | undefined) => {
    if (!category?.title) {
      return null;
    }

    if (category?.categories && category?.categories?.length > 0) {
      const hasValidLink = Boolean(category.linkType || category.code || category.href?.trim());

      return (
        <div className={styles.categoryItem}>
          <div className={styles.categoryContent}>
            {hasValidLink ? (
              <LinkWithLang
                type={category.linkType || OTHER_LINK_TYPE}
                data={category}
                className={styles.categoryText}
                onClick={handleClose}
                target={category.openInNewTab ? "_blank" : "_self"}
              >
                {category.title}
              </LinkWithLang>
            ) : (
              <p className={styles.categoryText}>{category.title}</p>
            )}
            <Button
              type="button"
              styleType="no-style"
              className={styles.chevronButton}
              onClick={() => setActiveCategory(category)}
              aria-label={`Expand ${category.title}`}
            >
              <span className={styles.chevronRight} />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div key={category.id} className={`${styles.childCategoryItem}`}>
        <div className={styles.childCategoryContent}>
          <LinkWithLang
            type={category.linkType || OTHER_LINK_TYPE}
            data={category}
            className={styles.categoryText}
            onClick={handleClose}
            activeClassName={(linkUrl) =>
              isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
            }
            target={category.openInNewTab ? "_blank" : "_self"}
          >
            <span className={styles.linkInline}>
              {category.title}
              {category.linkType === "external-link" && (
                <span className={styles.externalLinkIcon} />
              )}
            </span>
          </LinkWithLang>
        </div>
      </div>
    );
  };

  return (
    <ZendeskIconHideWrapper>
      <div className={styles.menuContainer}>
        <Button
          ref={overlayRef}
          className={styles.overlay}
          onClick={handleOverlayClick}
          type="button"
          aria-label="Close menu"
          styleType="no-style"
        />

        {/* Menu drawer */}
        <SlidePanel panelRef={menuRef} overlayRef={overlayRef} className={styles.menuDrawer}>
          <div className={styles.topSection}>
            {!isBrandListOpen && (
              <div className={styles.header}>
                {activeCategory || activeSubCategory ? (
                  <Button
                    styleType="no-style"
                    onClick={() => {
                      if (activeSubCategory) {
                        setActiveSubCategory(null);
                      } else {
                        setActiveCategory(null);
                      }
                    }}
                    className={styles.closeButton}
                    aria-label="Back"
                  >
                    <span className={styles.activeArrowIcon} />
                    <p className={styles.categoryText}>Back</p>
                  </Button>
                ) : (
                  <div />
                )}
                <Button
                  onClick={handleClose}
                  className={styles.closeButton}
                  aria-label="Close menu"
                  styleType="no-style"
                >
                  <span className={styles.closeButtonIcon} />
                </Button>
              </div>
            )}

            {/* Render categories dynamically */}
            {!isBrandListOpen && !activeCategory && !activeSubCategory && (
              <div>
                {/* Main categories */}
                {validHeaderNav?.map((nav) => renderCategoryButton(nav))}
              </div>
            )}
            {!activeCategory && !activeSubCategory && !isBrandListOpen && (
              <div className={styles.categoryItem}>
                <Button
                  type="button"
                  styleType="no-style"
                  className={styles.chevronButton}
                  innerChildClass={styles.brandListButton}
                  onClick={() => setIsBrandListOpen(true)}
                  aria-label="Expand Our Brands"
                >
                  <p className={styles.categoryText}>Our Brands</p>
                  <span className={styles.chevronRight} />
                </Button>
              </div>
            )}
            {isBrandListOpen && <BrandSlider onBack={handleBrandListClose} onClose={handleClose} />}
            {activeCategory && !activeSubCategory && (
              <>
                {/* Show parent categories only */}
                {activeCategory.categories!.map((category, index) => (
                  <div
                    key={category.id || category.label || index}
                    className={styles.childCategoryItem}
                  >
                    <div className={styles.childCategoryContent}>
                      <LinkWithLang
                        type={category.linkType || OTHER_LINK_TYPE}
                        data={category}
                        className={styles.categoryTextLight}
                        onClick={handleClose}
                        activeClassName={(linkUrl) =>
                          isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
                        }
                      >
                        {category.label}
                      </LinkWithLang>
                      <Button
                        styleType="no-style"
                        type="button"
                        onClick={() => setActiveSubCategory(category)}
                        aria-label={`Navigate to ${category.label}`}
                        className={styles.categoryButton}
                      >
                        <span className={styles.chevronRight} />
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {activeSubCategory && (
              <div className={styles.categoryLinks}>
                <div className={styles.linksSection}>
                  <ul className={styles.linksList}>
                    {activeSubCategory.links?.map((link, index) => (
                      <li key={link.id || link.label || index} className={styles.linkItem}>
                        <LinkWithLang
                          type={link.linkType || OTHER_LINK_TYPE}
                          data={link}
                          className={styles.quickLink}
                          onClick={handleClose}
                          activeClassName={(linkUrl) =>
                            isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
                          }
                        >
                          {link.label}
                        </LinkWithLang>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Quick Links */}
                {activeSubCategory?.quickLinks && activeSubCategory.quickLinks?.length > 0 ? (
                  <div className={styles.quickLinksSection}>
                    <h3 className={styles.viewAllText}>Quick Links</h3>
                    <ul className={styles.linksList}>
                      {activeSubCategory.quickLinks?.map((link, index) => (
                        <li key={link.id || link.label || index} className={styles.linkItem}>
                          <LinkWithLang
                            type={link.linkType || OTHER_LINK_TYPE}
                            data={link}
                            className={styles.quickLink}
                            onClick={handleClose}
                            activeClassName={(linkUrl) =>
                              isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
                            }
                          >
                            {link.label}
                          </LinkWithLang>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Dynamic section - only show when no active category or sub-category */}
          {!activeCategory && !activeSubCategory && !isBrandListOpen && (
            <HeaderMobileMenuActions profileText={profileText} ctaButtonData={ctaButtonData} />
          )}
        </SlidePanel>
      </div>
    </ZendeskIconHideWrapper>
  );
}
